import type { Size } from '../geometry/framing.ts';
import { estimateVideoBitrate } from './estimate.ts';
import type { QualityId } from './recipe.ts';

/**
 * What the finished edit is saved as. The choice is made in two steps: what
 * kind of thing (a video, a looping GIF, the sound alone, one picture), then
 * the file type, which only matters to the app it is opened in next.
 */
export type OutputKind = 'video' | 'gif' | 'audio' | 'image';
export const OUTPUT_KINDS: readonly OutputKind[] = ['video', 'gif', 'audio', 'image'];

export type OutputFormatId = 'mp4' | 'webm' | 'mov' | 'gif' | 'm4a' | 'mp3' | 'wav' | 'png' | 'jpg';

export interface OutputFormat {
  readonly id: OutputFormatId;
  readonly kind: OutputKind;
  readonly extension: string;
  readonly mimeType: string;
  /** Whether a Small/Balanced/High choice changes anything. */
  readonly hasQuality: boolean;
}

export const OUTPUT_FORMATS: Readonly<Record<OutputFormatId, OutputFormat>> = {
  mp4: { id: 'mp4', kind: 'video', extension: 'mp4', mimeType: 'video/mp4', hasQuality: true },
  webm: { id: 'webm', kind: 'video', extension: 'webm', mimeType: 'video/webm', hasQuality: true },
  mov: { id: 'mov', kind: 'video', extension: 'mov', mimeType: 'video/quicktime', hasQuality: true },
  gif: { id: 'gif', kind: 'gif', extension: 'gif', mimeType: 'image/gif', hasQuality: true },
  m4a: { id: 'm4a', kind: 'audio', extension: 'm4a', mimeType: 'audio/mp4', hasQuality: true },
  mp3: { id: 'mp3', kind: 'audio', extension: 'mp3', mimeType: 'audio/mpeg', hasQuality: true },
  wav: { id: 'wav', kind: 'audio', extension: 'wav', mimeType: 'audio/wav', hasQuality: false },
  png: { id: 'png', kind: 'image', extension: 'png', mimeType: 'image/png', hasQuality: false },
  jpg: { id: 'jpg', kind: 'image', extension: 'jpg', mimeType: 'image/jpeg', hasQuality: true },
};

/** File types of each kind, the one that opens most widely first. */
export const FORMATS_BY_KIND: Readonly<Record<OutputKind, readonly OutputFormatId[]>> = {
  video: ['mp4', 'webm', 'mov'],
  gif: ['gif'],
  audio: ['m4a', 'mp3', 'wav'],
  image: ['jpg', 'png'],
};

export const DEFAULT_OUTPUT: OutputFormatId = 'mp4';

export const formatOf = (id: OutputFormatId): OutputFormat => OUTPUT_FORMATS[id];
export const kindOf = (id: OutputFormatId): OutputKind => OUTPUT_FORMATS[id].kind;
/** How a format is named on screen: its extension in capitals ("MP4", "JPG"). */
export const formatName = (id: OutputFormatId): string => OUTPUT_FORMATS[id].extension.toUpperCase();
/** Formats that play over time (everything but a single picture). */
export const isMoving = (id: OutputFormatId): boolean => kindOf(id) !== 'image';
export const hasPicture = (id: OutputFormatId): boolean => kindOf(id) !== 'audio';
export const carriesSound = (id: OutputFormatId): boolean => kindOf(id) === 'video' || kindOf(id) === 'audio';

/** A stored format, or the default when it is missing or unknown. */
export const parseOutputFormat = (value: unknown): OutputFormatId => (typeof value === 'string' && value in OUTPUT_FORMATS ? (value as OutputFormatId) : DEFAULT_OUTPUT);

/** The file type to switch to when the kind changes: the current one if it is of that kind already. */
export const formatForKind = (kind: OutputKind, current: OutputFormatId): OutputFormatId => (kindOf(current) === kind ? current : FORMATS_BY_KIND[kind][0]!);

/** A file name for the result, from the opened video's name. */
export function outputFileName(sourceName: string, format: OutputFormatId = DEFAULT_OUTPUT): string {
  const base = sourceName.replace(/\.[^.]+$/, '').replace(/[^\p{L}\p{N}_-]+/gu, '-').replace(/^-+|-+$/g, '') || 'video';
  return `${base}-shycut.${OUTPUT_FORMATS[format].extension}`;
}

/** Characters no file system accepts, and those that would make a hidden or relative name. */
const UNSAFE_NAME = /[\\/:*?"<>|\u0000-\u001f]+/g;
const MAX_NAME_LENGTH = 120;

/**
 * A name the person typed, made safe to save: no path or reserved characters,
 * not too long, and always ending in the format's own extension.
 */
export function withExtension(name: string, format: OutputFormatId): string {
  const extension = OUTPUT_FORMATS[format].extension;
  const known = new Set(Object.values(OUTPUT_FORMATS).map((entry) => entry.extension));
  const trimmed = name.replace(UNSAFE_NAME, '-').trim();
  const dot = trimmed.lastIndexOf('.');
  const stem = dot > 0 && known.has(trimmed.slice(dot + 1).toLowerCase()) ? trimmed.slice(0, dot) : trimmed;
  const safe = stem.replace(/^[.\s-]+/, '').slice(0, MAX_NAME_LENGTH).trim() || 'video-shycut';
  return `${safe}.${extension}`;
}

/** The part of a file name before its extension, for editing. */
export const nameStem = (name: string): string => name.replace(/\.[a-z0-9]{2,4}$/i, '');

// ——— GIF ———

/** A GIF is a loop for chats and pages: longer ones get huge and unwieldy. */
export const GIF_MAX_SEC = 60;

/** Frame rate and longest side of a GIF at each quality. */
export const GIF_SETTINGS: Readonly<Record<QualityId, { readonly fps: number; readonly longSide: number }>> = {
  small: { fps: 10, longSide: 480 },
  balanced: { fps: 12, longSide: 640 },
  high: { fps: 15, longSide: 800 },
};

/** `size` shrunk (never grown) so its longer side is at most `longSide`, in even pixels. */
export function fitLongSide(size: Size, longSide: number): Size {
  const scale = Math.min(1, longSide / Math.max(size.width, size.height, 1));
  const even = (value: number) => Math.max(2, Math.round((value * scale) / 2) * 2);
  return { width: even(size.width), height: even(size.height) };
}

/**
 * Centiseconds each GIF frame is shown, from where the frames fall in time:
 * GIF counts in hundredths of a second, so rounding the running total (not
 * each frame) keeps the loop as long as the video.
 */
export function gifDelays(times: readonly number[], end: number): number[] {
  return times.map((time, index) => gifDelay(time, times[index + 1] ?? end));
}

/** Centiseconds a frame shown from `from` until `to` lasts (players treat less than 2 as 10, so never less). */
export const gifDelay = (from: number, to: number): number => Math.max(2, Math.round(to * 100) - Math.round(from * 100));

// ——— Sound ———

/** Bits per second of compressed sound at each quality (WAV is uncompressed). */
export const AUDIO_BITRATES: Readonly<Record<QualityId, number>> = { small: 96_000, balanced: 160_000, high: 256_000 };

/** JPEG quality at each quality setting. */
export const JPEG_QUALITY: Readonly<Record<QualityId, number>> = { small: 0.82, balanced: 0.9, high: 0.96 };

// ——— Size and limits ———

export interface OutputMeasure {
  /** Picture size of the edit's output. */
  readonly size: Size;
  readonly frameRate: number;
  readonly quality: QualityId;
  readonly durationSec: number;
  readonly hasAudio: boolean;
  /** Channels and sample rate of the sound (for WAV). */
  readonly channels?: number;
  readonly sampleRate?: number;
}

/** Bytes per pixel of a GIF frame from video (LZW over 256 colours compresses camera footage poorly). */
const GIF_BYTES_PER_PIXEL = 0.32;

/** Rough file size in bytes, for the summary before saving. */
export function estimateOutputSize(format: OutputFormatId, measure: OutputMeasure): number {
  const duration = Math.max(0, measure.durationSec);
  const pixels = measure.size.width * measure.size.height;
  switch (format) {
    case 'gif': {
      const gif = GIF_SETTINGS[measure.quality];
      const frame = fitLongSide(measure.size, gif.longSide);
      return Math.round(frame.width * frame.height * GIF_BYTES_PER_PIXEL * gif.fps * Math.min(duration, GIF_MAX_SEC));
    }
    case 'm4a':
    case 'mp3':
      return measure.hasAudio ? Math.round((AUDIO_BITRATES[measure.quality] * duration) / 8) : 0;
    case 'wav':
      return measure.hasAudio ? Math.round(duration * (measure.sampleRate ?? 48_000) * (measure.channels ?? 2) * 2) : 0;
    case 'png':
      return Math.round(pixels * 1.6);
    case 'jpg':
      return Math.round(pixels * (measure.quality === 'small' ? 0.12 : measure.quality === 'balanced' ? 0.2 : 0.32));
    default: {
      // VP9 gets about the same picture from ~30% fewer bits than H.264.
      const efficiency = format === 'webm' ? 0.7 : 1;
      const video = estimateVideoBitrate(measure.size, measure.frameRate, measure.quality) * efficiency;
      const audio = measure.hasAudio ? AUDIO_BITRATES.balanced * 0.8 : 0;
      return Math.round(((video + audio) * duration) / 8);
    }
  }
}

/** Why the edit can't be saved in a format as it is, or null when it can. */
export type OutputProblem = 'gif-too-long' | 'no-sound';

export function outputProblem(format: OutputFormatId, edit: { readonly durationSec: number; readonly hasAudio: boolean }): OutputProblem | null {
  if (format === 'gif' && edit.durationSec > GIF_MAX_SEC + 0.05) return 'gif-too-long';
  if (kindOf(format) === 'audio' && !edit.hasAudio) return 'no-sound';
  return null;
}
